import { useMemo } from 'react';
import { useTheme } from '@mui/material';
import L from 'leaflet';

/**
 * A map pin in the theme's primary color.
 *
 * The theme already takes the group's primary_color on a branded subdomain (App.js), so pins follow the brand with no
 * extra data; on the multi-tenant portal they use the default primary. Replaces the fixed blue PNG, which looked like
 * a bug on a portal themed any other color.
 *
 * Memoized on the color: building a Leaflet icon inside the markers' map() would make one per marker per render.
 */
// The color is written into raw HTML (Leaflet's divIcon), and a group's primary_color is free text on the server, so
// only a hex color is let through. WHY: anything else could carry markup into every member's page on that portal.
const HEX_COLOR = /^#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const FALLBACK_PIN_COLOR = '#1976d2';

const useBrandPinIcon = () => {
  const primary = useTheme().palette.primary.main;
  const color = HEX_COLOR.test(primary) ? primary : FALLBACK_PIN_COLOR;

  return useMemo(
    () =>
      L.divIcon({
        // An empty className drops Leaflet's default white square behind div icons.
        className: '',
        // The SVG is drawn at the viewBox's own proportions so the tip lands exactly on iconAnchor; a box of another
        // shape would letterbox the drawing and float the tip above the location.
        html: `<svg width="24" height="34" viewBox="0 0 24 34" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M12 0C5.4 0 0 5.3 0 11.9 0 20.8 12 34 12 34s12-13.2 12-22.1C24 5.3 18.6 0 12 0z" fill="${color}"/>
          <circle cx="12" cy="11.8" r="4.4" fill="#fff"/>
        </svg>`,
        iconSize: [24, 34],
        iconAnchor: [12, 34],
        popupAnchor: [0, -30],
      }),
    [color],
  );
};

export default useBrandPinIcon;
